import { useCallback, useEffect, useState } from 'react'
import { userRepository } from '@/data/users/repositories/user_repository_impl'
import type { UserListItem } from '@/domain/users/entities/user.entity'
import { DeleteUserUseCase } from '@/domain/users/usecases/delete_user.usecase'
import { GetUsersUseCase } from '@/domain/users/usecases/get_users.usecase'
import { toAppError } from '@/core/errors/app_error'
import { logError } from '@/utils/logger'
import { matchUserListItem } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'
import {
  initialDeleteDialogState,
  type UserSortField,
  type UsersDeleteDialogState,
} from '../models/users_ui_state'

const defaultGetUsers = new GetUsersUseCase(userRepository)
const defaultDeleteUser = new DeleteUserUseCase(userRepository)

export interface UseUsersViewModelDeps {
  getUsers?: GetUsersUseCase
  deleteUser?: DeleteUserUseCase
}

export function useUsersViewModel(deps: UseUsersViewModelDeps = {}) {
  const getUsers = deps.getUsers ?? defaultGetUsers
  const deleteUser = deps.deleteUser ?? defaultDeleteUser

  const [users, setUsers] = useState<UserListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<UsersDeleteDialogState>(
    initialDeleteDialogState
  )
  const [isDeleting, setIsDeleting] = useState(false)

  const { table, actions: tableActions } = useTableQueryState({
    items: users,
    matchesSearch: matchUserListItem,
  })

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const list = await getUsers.execute({ page: 0, limit: 100 })
      setUsers(list)
    } catch (err: unknown) {
      logError('Failed to fetch users', err)
      setError(toAppError(err, 'Failed to load users').message)
      setUsers([])
    } finally {
      setIsLoading(false)
    }
  }, [getUsers])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openDeleteDialog = useCallback((userId: string, userName: string) => {
    setDeleteDialog({ isOpen: true, userId, userName })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(initialDeleteDialogState)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.userId) return

    try {
      setIsDeleting(true)
      await deleteUser.execute(deleteDialog.userId)
      setUsers(prev => prev.filter(user => user.id !== deleteDialog.userId))
      closeDeleteDialog()
    } catch (err: unknown) {
      logError('Failed to delete user', err)
      alert(toAppError(err, 'Failed to delete user. Please try again.').message)
    } finally {
      setIsDeleting(false)
    }
  }, [deleteDialog.userId, deleteUser, closeDeleteDialog])

  const handleSort = useCallback(
    (field: UserSortField) => tableActions.handleSort(field),
    [tableActions]
  )

  return {
    state: {
      users,
      filteredUsers: table.filteredItems,
      paginatedUsers: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      deleteDialog,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    error,
    isDeleting,
    actions: {
      refresh,
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
    },
  }
}
